/**
 * Jun-2026 (User-Wunsch · a) — VEREINTE Boni-Nachverfolgung (Buchhaltung-Tab).
 *
 * Führt die zuvor getrennten Tabs „Boni-Nachverfolgung" (manuell in der
 * Disposition erfasste Boni) und „Hersteller-Boni" (beim Verkauf automatisch in
 * die Warenwirtschaft eingebuchte externe Hersteller-Aktionen) in EINEM Tab
 * zusammen. Umschalter „Gruppieren nach: Fahrzeug | Hersteller" aus derselben
 * deduplizierten Datenbasis. Funktionen (Überfälligkeit, CSV/PDF-Export,
 * Erinnerungen, „Eingegangen markieren") gelten für ALLE Boni.
 *
 * Quelle: GET /api/dealer/boni-overview (status-Filter optional).
 * Status setzen: POST /api/dealer/boni/mark-received.
 */
import { useEffect, useState, useCallback } from "react";
import { Gift, Loader2, CheckCircle2, Clock, AlertTriangle, Download, FileText, BellRing, Banknote, Bike, Factory } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";

const STATUS_FILTERS = [
    { key: "", label: "Alle" },
    { key: "offen", label: "Offen" },
    { key: "eingegangen", label: "Eingegangen" },
];
const GROUP_MODES = [
    { key: "vehicle", label: "Fahrzeug", icon: Bike },
    { key: "manufacturer", label: "Hersteller", icon: Factory },
];
const OVERDUE_LABEL = { 1: "1. Erinnerung", 2: "2. Erinnerung", 3: "3. Erinnerung" };

function StatusBadge({ status }) {
    if (status === "eingegangen") {
        return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> Eingegangen</span>;
    }
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5"><Clock className="w-3 h-3" /> Offen</span>;
}

function SourceBadge({ label }) {
    if (!label) return null;
    const isManu = label.includes("Hersteller");
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-sm px-1.5 py-0.5 border ${isManu ? "text-sky-700 bg-sky-50 border-sky-200" : "text-violet-700 bg-violet-50 border-violet-200"}`} data-testid="boni-source-badge">
            {label}
        </span>
    );
}

function OverdueBadge({ level, days }) {
    if (!level) return null;
    return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5" data-testid="boni-overdue-badge" title={`Überfällig seit ${days} Tagen`}>
            <AlertTriangle className="w-3 h-3" /> Überfällig · {OVERDUE_LABEL[level]} ({days} T.)
        </span>
    );
}

export default function BoniTrackingTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [groupMode, setGroupMode] = useState("vehicle");
    const [exporting, setExporting] = useState("");
    const [reminding, setReminding] = useState(false);
    const [busy, setBusy] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        api.get("/dealer/boni-overview", { params: statusFilter ? { status: statusFilter } : {} })
            .then((r) => setData(r.data))
            .catch(() => setData({ vehicle_groups: [], manufacturer_groups: [], total_open: 0, total_received: 0, overdue_total: 0 }))
            .finally(() => setLoading(false));
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const markReceived = async (e) => {
        setBusy(e.id);
        try {
            await api.post("/dealer/boni/mark-received", {
                listing_id: e.listing_id, article_id: e.article_id || "", entry_id: e.entry_id || "", status: "eingegangen",
            });
            toast.success("Als eingegangen markiert");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Status konnte nicht gesetzt werden");
        } finally {
            setBusy("");
        }
    };

    const doExport = async (format) => {
        setExporting(format);
        try {
            const res = await api.get("/dealer/bonus-tracking/export", {
                params: { format, ...(statusFilter ? { status: statusFilter } : {}) },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `boni-nachverfolgung.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`${format.toUpperCase()}-Export erstellt`);
        } catch {
            toast.error("Export fehlgeschlagen");
        } finally {
            setExporting("");
        }
    };

    const sendReminders = async () => {
        setReminding(true);
        try {
            const r = await api.post("/dealer/bonus-tracking/send-reminders");
            if (!r.data?.count) toast.info("Keine überfälligen Boni vorhanden.");
            else toast.success(`${r.data.count} Erinnerung(en) vorbereitet. ${r.data.message || ""}`);
        } catch {
            toast.error("Erinnerungen konnten nicht gesendet werden");
        } finally {
            setReminding(false);
        }
    };

    const overdue = data?.overdue_total || 0;
    const groups = groupMode === "manufacturer" ? (data?.manufacturer_groups || []) : (data?.vehicle_groups || []);

    // Eingegangen-Button + Quelle: nur sinnvoll, wenn die Aktion gesetzt werden kann.
    const renderActionCell = (e) => {
        if (e.status === "eingegangen") {
            return <span className="text-[10px] text-gray-400">{e.date ? String(e.date).slice(0, 10).split("-").reverse().join(".") : "—"}</span>;
        }
        return (
            <button type="button" onClick={() => markReceived(e)} disabled={busy === e.id}
                className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-600 text-white rounded-sm px-2 py-1 hover:bg-emerald-700 disabled:opacity-50"
                data-testid={`boni-mark-received-${e.id}`}>
                {busy === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Banknote className="w-3 h-3" />} Eingegangen
            </button>
        );
    };

    return (
        <div data-testid="boni-tracking-tab">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-amber-500" />
                    <h3 className="font-display font-bold text-base">Boni-Nachverfolgung</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => doExport("csv")} disabled={!!exporting} className="inline-flex items-center gap-1 text-xs font-bold border border-gray-300 rounded-sm px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid="boni-export-csv">
                        {exporting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} CSV
                    </button>
                    <button type="button" onClick={() => doExport("pdf")} disabled={!!exporting} className="inline-flex items-center gap-1 text-xs font-bold border border-gray-300 rounded-sm px-2.5 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid="boni-export-pdf">
                        {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
                    </button>
                    <button type="button" onClick={sendReminders} disabled={reminding} className="inline-flex items-center gap-1 text-xs font-bold bg-[#111827] text-white rounded-sm px-2.5 py-1.5 hover:bg-black disabled:opacity-50" data-testid="boni-send-reminders">
                        {reminding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Erinnerungen senden
                    </button>
                </div>
            </div>
            <p className="text-[11px] text-gray-400 mb-4">
                Vereint manuell erfasste Boni (Disposition → „Zahlungseingänge / Boni") und beim Verkauf automatisch eingebuchte externe Hersteller-Aktionen — dedupliziert.
            </p>

            {/* Summen */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="boni-tracking-sum-open">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Erwartet / offen</div>
                    <div className="font-display font-black text-xl text-amber-800 font-mono-num">{formatPrice(data?.total_open || 0)}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="boni-tracking-sum-received">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Eingegangen</div>
                    <div className="font-display font-black text-xl text-emerald-800 font-mono-num">{formatPrice(data?.total_received || 0)}</div>
                </div>
                <div className={`border rounded-sm p-3 ${overdue ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`} data-testid="boni-tracking-overdue">
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${overdue ? "text-red-700" : "text-gray-500"}`}>Überfällig</div>
                    <div className={`font-display font-black text-xl font-mono-num ${overdue ? "text-red-800" : "text-[#111827]"}`}>{overdue}</div>
                    {overdue > 0 && data?.overdue_breakdown && (
                        <div className="text-[10px] text-red-600 font-mono-num">
                            {data.overdue_breakdown.stufe1}·60T / {data.overdue_breakdown.stufe2}·90T / {data.overdue_breakdown.stufe3}·120T
                        </div>
                    )}
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{groupMode === "manufacturer" ? "Hersteller" : "Fahrzeuge mit Boni"}</div>
                    <div className="font-display font-black text-xl text-[#111827] font-mono-num">{groupMode === "manufacturer" ? (data?.manufacturer_count || 0) : (data?.vehicle_count || 0)}</div>
                </div>
            </div>

            {/* Steuerzeile: Gruppieren nach + Statusfilter */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2" data-testid="boni-group-toggle">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gruppieren nach</span>
                    <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden">
                        {GROUP_MODES.map((m) => {
                            const Icon = m.icon;
                            return (
                                <button key={m.key} type="button" onClick={() => setGroupMode(m.key)}
                                    className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 transition-colors ${groupMode === m.key ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                                    data-testid={`boni-group-${m.key}`}>
                                    <Icon className="w-3.5 h-3.5" /> {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="flex items-center gap-1" data-testid="boni-tracking-filters">
                    {STATUS_FILTERS.map((f) => (
                        <button key={f.key} type="button" onClick={() => setStatusFilter(f.key)}
                            className={`text-xs font-bold px-3 py-1.5 rounded-sm border transition-colors ${statusFilter === f.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                            data-testid={`boni-tracking-filter-${f.key || "all"}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>
            ) : !groups.length ? (
                <div className="text-center py-10 text-gray-400 text-sm" data-testid="boni-tracking-empty">Keine verbuchten Boni gefunden.</div>
            ) : (
                <div className="space-y-4" data-testid="boni-tracking-groups">
                    {groups.map((g) => {
                        const isManu = groupMode === "manufacturer";
                        const headKey = isManu ? g.manufacturer : g.listing_id;
                        return (
                            <div key={headKey} className="border border-gray-200 rounded-sm overflow-hidden" data-testid={`boni-tracking-group-${headKey}`}>
                                <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 border-b border-gray-200 px-3 py-2">
                                    <div className="min-w-0 flex items-center gap-2">
                                        {isManu && <Factory className="w-4 h-4 text-gray-400 shrink-0" />}
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm truncate">{isManu ? g.manufacturer : g.vehicle}</div>
                                            {!isManu && (
                                                <div className="text-[11px] text-gray-500 font-mono-num">
                                                    FIN: {g.vin || "—"}{g.internal_number ? ` · Nr. ${g.internal_number}` : ""}{g.condition ? ` · ${g.condition}` : ""}
                                                </div>
                                            )}
                                            {isManu && <span className="text-[10px] text-gray-400">{g.entries.length} Aktion(en)</span>}
                                        </div>
                                    </div>
                                    <div className="text-[11px] font-mono-num text-right shrink-0">
                                        <span className="text-amber-700 font-bold">{formatPrice(g.open)} offen</span>
                                        <span className="text-gray-300 mx-1">|</span>
                                        <span className="text-emerald-700 font-bold">{formatPrice(g.received)} erhalten</span>
                                    </div>
                                </div>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                                            <th className="px-3 py-1.5">Boni / Aktion</th>
                                            {isManu && <th className="px-3 py-1.5">Fahrzeug</th>}
                                            <th className="px-3 py-1.5 text-right">Betrag</th>
                                            <th className="px-3 py-1.5">Status</th>
                                            {!isManu && <th className="px-3 py-1.5">Eingangsdatum</th>}
                                            {!isManu && <th className="px-3 py-1.5">Belegnummer</th>}
                                            <th className="px-3 py-1.5 text-right">Aktion</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {g.entries.map((e) => (
                                            <tr key={e.id} className="border-b border-gray-50" data-testid={`boni-tracking-row-${e.id}`}>
                                                <td className="px-3 py-1.5">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="font-medium">{e.name}</span>
                                                        <SourceBadge label={e.source_label} />
                                                    </div>
                                                    {e.status !== "eingegangen" && e.overdue_level > 0 && (
                                                        <div className="mt-0.5"><OverdueBadge level={e.overdue_level} days={e.age_days} /></div>
                                                    )}
                                                </td>
                                                {isManu && (
                                                    <td className="px-3 py-1.5 text-gray-600">
                                                        <span className="truncate">{e.vehicle}</span>
                                                        {e.internal_number && <span className="text-[10px] text-gray-400 font-mono-num"> · Nr. {e.internal_number}</span>}
                                                    </td>
                                                )}
                                                <td className="px-3 py-1.5 text-right font-mono-num font-bold">{formatPrice(e.amount)}</td>
                                                <td className="px-3 py-1.5"><StatusBadge status={e.status} /></td>
                                                {!isManu && <td className="px-3 py-1.5 font-mono-num text-gray-600">{e.date ? String(e.date).slice(0, 10).split("-").reverse().join(".") : "—"}</td>}
                                                {!isManu && <td className="px-3 py-1.5 font-mono-num text-gray-600">{e.belegnummer || "—"}</td>}
                                                <td className="px-3 py-1.5 text-right">{renderActionCell(e)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
